/* Manual directory-group membership admin surface — TEMPORARY.
   Stakeholder-demo bridge, ahead of PB-63 (Platform People & Organization
   Directory sync/UI). Lets a tenant admin add/remove a tenant member from a
   directory group by hand, plus a bare-bones `createDirectoryGroup` for
   environments with no groups yet (no sync configured). Reads and writes the
   same `directory_groups` / `directory_group_members` tables the LAMA
   campaign scope step already resolves through, so a change made here is
   immediately visible there. Delete this file (and the page/route that call
   it) once PB-63 lands. */

import { AdminApiError, type TenantRole } from "./admin-users-api.js";
import { csrfHeaders } from "../auth/session-api.js";

const BASE = "/api/auth/admin/directory-groups";

/** `directory_groups_source_check` (migration 202608040001). */
export type DirectoryGroupSource = "admin" | "microsoft_entra" | "scim";

export interface DirectoryGroupOption {
  groupId: string;
  displayName: string;
  source: DirectoryGroupSource;
  memberCount: number;
  /** Unrelated concept (PB-136 role-mapping); ignored by this surface. */
  mappedRole: TenantRole | null;
}

export interface DirectoryGroupOptionPage {
  groups: DirectoryGroupOption[];
  nextCursor: string | null;
}

export interface DirectoryGroupMember {
  userId: string;
  displayName: string | null;
  email: string | null;
  /** Only `"admin"` may be removed through this page's Remove action — a
      `"scim"`/`"microsoft_entra"` row is owned by directory sync, which would
      silently restore it. */
  source: DirectoryGroupSource;
}

export interface DirectoryGroupMemberPage {
  members: DirectoryGroupMember[];
  truncated: boolean;
}

/**
 * Hard cap on pages walked by {@link listAllDirectoryGroups}. A tenant with more
 * groups than this is an operational outlier, and an unbounded loop over a
 * cursor the server controls is a worse failure than a bounded one.
 */
const MAX_DIRECTORY_GROUP_PAGES = 20;

/**
 * EVERY page of the tenant's active groups.
 *
 * The picker called {@link listDirectoryGroups} with no cursor and rendered only
 * what came back, so once a tenant had more groups than one server page an admin
 * simply could not select the rest — the response carried `nextCursor` and
 * nothing followed it. Walks pages until the cursor runs out or the page cap is
 * reached, whichever comes first.
 */
export async function listAllDirectoryGroups(
  options: { query?: string } = {},
): Promise<DirectoryGroupOptionPage> {
  const groups: DirectoryGroupOption[] = [];
  let cursor: string | null | undefined = undefined;
  for (let page = 0; page < MAX_DIRECTORY_GROUP_PAGES; page += 1) {
    const next: DirectoryGroupOptionPage = await listDirectoryGroups({
      ...(options.query === undefined ? {} : { query: options.query }),
      ...(cursor === undefined ? {} : { cursor }),
    });
    groups.push(...next.groups);
    cursor = next.nextCursor;
    if (!cursor) return { groups, nextCursor: null };
  }
  /* Cap reached with a cursor still outstanding. Reported rather than silently
     truncated: the caller can say so, and "the list is incomplete" is a very
     different thing from "that is all the groups". */
  return { groups, nextCursor: cursor ?? null };
}

/** ONE page of the tenant's active groups. Prefer {@link listAllDirectoryGroups}
 *  for a picker — a single page silently hides the remainder. */
export async function listDirectoryGroups(
  options: { query?: string; cursor?: string | null } = {},
): Promise<DirectoryGroupOptionPage> {
  const params = new URLSearchParams();
  if (options.query) params.set("query", options.query);
  if (options.cursor) params.set("cursor", options.cursor);
  const query = params.toString();
  return (await request(
    query ? `${BASE}?${query}` : BASE,
  )) as DirectoryGroupOptionPage;
}

export async function listDirectoryGroupMembers(
  groupId: string,
): Promise<DirectoryGroupMemberPage> {
  return (await request(
    `${BASE}/${encodeURIComponent(groupId)}/members`,
  )) as DirectoryGroupMemberPage;
}

export async function addDirectoryGroupMember(
  groupId: string,
  userId: string,
): Promise<{ added: boolean }> {
  return (await request(`${BASE}/${encodeURIComponent(groupId)}/members`, {
    method: "POST",
    body: JSON.stringify({ userId }),
  })) as { added: boolean };
}

export async function createDirectoryGroup(
  displayName: string,
): Promise<{ groupId: string; displayName: string }> {
  return (await request(BASE, {
    method: "POST",
    body: JSON.stringify({ displayName }),
  })) as { groupId: string; displayName: string };
}

export async function removeDirectoryGroupMember(
  groupId: string,
  userId: string,
): Promise<{ removed: boolean }> {
  return (await request(
    `${BASE}/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  )) as { removed: boolean };
}

async function request(path: string, init: RequestInit = {}): Promise<unknown> {
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...csrfHeaders(),
      ...init.headers,
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      code?: string;
      message?: string;
    } | null;
    throw new AdminApiError(
      body?.message ?? body?.code ?? `Request failed (${response.status})`,
      body?.code,
    );
  }
  return response.status === 204 ? null : response.json();
}
